import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { Box, Checkbox, Collapse, Divider, IconButton, styled, Tooltip, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import moment from "moment";
import { IFlight } from "@vmosolutions/oc-interfaces";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { isFlight } from "src/redux/reducers/FlightTimelineReducer";
import { FlightChip, IFlightChip } from "src/components/Flights/FlightChip";
import { useDrag } from "react-dnd";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** How often (ms) the "now" reference updates for time-based filtering. */
const NOW_REFRESH_INTERVAL_MS = 60_000;

/** Lookahead window in milliseconds (24 hours). */
const LOOKAHEAD_MS = 24 * 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const PanelRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  height: "100%",
  borderLeft: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}));

const ToggleStrip = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  paddingTop: theme.spacing(1),
  width: 28,
  minWidth: 28,
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const PanelContent = styled(Box)(({ theme }) => ({
  width: 280,
  minWidth: 280,
  maxWidth: 280,
  height: "100%",
  overflow: "auto",
  padding: theme.spacing(1),
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.7rem",
  fontWeight: 700,
  textTransform: "uppercase",
  color: theme.palette.text.secondary,
  letterSpacing: "0.08em",
  marginBottom: theme.spacing(0.25),
}));

const GroupBox = styled(Box)(({ theme }) => ({
  border: "1px solid",
  borderColor: theme.palette.divider,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.75),
  marginBottom: theme.spacing(0.5),
  backgroundColor: "rgba(255,255,255,0.03)",
}));

const SelectionDragBar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5, 1),
  marginBottom: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.primary.main}`,
  backgroundColor: theme.palette.action.selected,
  cursor: "grab",
  "&:active": { cursor: "grabbing" },
  userSelect: "none",
}));

// ---------------------------------------------------------------------------
// Sub-component: Draggable flight item with checkbox
// ---------------------------------------------------------------------------

interface IDraggableFlightItemProps {
  flight: IFlight;
  groupIndex: number;
  registration: string;
  isSelected: boolean;
  selectedFlights: IFlight[];
  onToggle: (flightId: string, registration: string, groupIndex: number, shiftKey: boolean) => void;
  tooltipContent: React.ReactNode;
  chipData: IFlightChip;
}

function DraggableFlightItem({
  flight,
  groupIndex,
  registration,
  isSelected,
  selectedFlights,
  onToggle,
  tooltipContent,
  chipData,
}: IDraggableFlightItemProps): JSX.Element {
  // When this flight is selected and there are multiple selections, drag all.
  // Otherwise drag just this one.
  const [, drag] = useDrag(
    () => ({
      type: isSelected && selectedFlights.length > 1 ? "SELECTED_FLIGHTS" : "UNASSIGNED_FLIGHT",
      item: () => {
        if (isSelected && selectedFlights.length > 1) {
          return {
            type: "SELECTED_FLIGHTS",
            selectedFlights: selectedFlights.map((f) => ({ _id: f._id })),
          };
        }
        return { type: "UNASSIGNED_FLIGHT", flight: chipData };
      },
    }),
    [isSelected, selectedFlights, chipData]
  );

  const handleCheckboxClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggle(flight._id, registration, groupIndex, e.shiftKey);
    },
    [flight._id, registration, groupIndex, onToggle]
  );

  return (
    <Box
      ref={drag}
      sx={{
        display: "flex",
        alignItems: "center",
        cursor: "grab",
        "&:active": { cursor: "grabbing" },
        borderRadius: 0.5,
        backgroundColor: isSelected ? "action.selected" : "transparent",
        transition: "background-color 0.15s ease",
      }}
    >
      <Checkbox size="small" checked={isSelected} onClick={handleCheckboxClick} sx={{ p: 0.25, mr: 0.25 }} />
      <FlightChip data={chipData} disableDrag disableContext tooltipContent={tooltipContent} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: Drag bar for bulk-assigning selected flights
// ---------------------------------------------------------------------------

interface ISelectionDragBarProps {
  selectedFlights: IFlight[];
  onClear: () => void;
}

function SelectionBar({ selectedFlights, onClear }: ISelectionDragBarProps): JSX.Element {
  const [, drag] = useDrag(
    () => ({
      type: "SELECTED_FLIGHTS",
      item: {
        type: "SELECTED_FLIGHTS",
        selectedFlights: selectedFlights.map((f) => ({ _id: f._id })),
      },
    }),
    [selectedFlights]
  );

  return (
    <SelectionDragBar ref={drag}>
      <DragIndicatorIcon fontSize="small" color="primary" />
      <Typography variant="caption" fontWeight={700} sx={{ flex: 1 }}>
        Drag {selectedFlights.length} flight{selectedFlights.length !== 1 ? "s" : ""} to a desk
      </Typography>
      <Tooltip title="Clear selection">
        <Typography
          variant="caption"
          color="primary"
          sx={{ cursor: "pointer", textDecoration: "underline" }}
          onClick={onClear}
        >
          Clear
        </Typography>
      </Tooltip>
    </SelectionDragBar>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convert a flight time value (number | Date | string) to epoch ms. */
const toEpoch = (t: number | Date | string | undefined | null): number | null => {
  if (t == null) return null;
  if (typeof t === "number") return t;
  const ms = new Date(t).getTime();
  return Number.isNaN(ms) ? null : ms;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function UnassignedFlightsPanel(): JSX.Element {
  const [expanded, setExpanded] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const lastClickedRef = useRef<{ registration: string; groupIndex: number } | null>(null);

  // Refresh "now" every 60 seconds so the time-based filter stays current.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), NOW_REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  // ---------------------------------------------------------------------------
  // Redux selectors
  // ---------------------------------------------------------------------------

  const flightData = useSelector((state: IRootState) => state.timelineFlights.flightData);
  const desks = useSelector((state: IRootState) => state.dispatchDesks.desks);

  // ---------------------------------------------------------------------------
  // Derive: flight IDs already assigned to any desk
  // ---------------------------------------------------------------------------

  const assignedFlightIds: Set<string> = useMemo(() => {
    const ids = new Set<string>();
    for (const desk of desks) {
      for (const f of desk.flights) {
        ids.add(f._id);
      }
    }
    return ids;
  }, [desks]);

  // ---------------------------------------------------------------------------
  // Derive: unassigned flights within the 24-hour window
  // ---------------------------------------------------------------------------

  const unassignedFlights: IFlight[] = useMemo(() => {
    const cutoff = now + LOOKAHEAD_MS;
    const results: IFlight[] = [];

    for (const aircraftId of Object.keys(flightData)) {
      const events = flightData[aircraftId];
      if (!events?.flights) continue;

      for (const flight of events.flights) {
        // Skip non-flight events (shouldn't happen but be safe)
        if (!isFlight(flight)) continue;

        // Skip flights already on a desk
        if (assignedFlightIds.has(flight._id)) continue;

        // Skip removed/cancelled flights
        if (flight.removalType) continue;

        const off = toEpoch(flight.actualOff);
        const on = toEpoch(flight.actualOn);
        if (off == null || on == null) continue;

        // Filter: actualOff between now and now+24h
        //         AND actualOn between now and now+24h
        if (off >= now && off <= cutoff && on >= now && on <= cutoff) {
          results.push(flight);
        }
      }
    }

    // Sort by departure time ascending
    results.sort((a, b) => {
      const tA = toEpoch(a.actualOff) ?? Infinity;
      const tB = toEpoch(b.actualOff) ?? Infinity;
      return tA - tB;
    });

    return results;
  }, [flightData, assignedFlightIds, now]);

  // ---------------------------------------------------------------------------
  // Prune selected IDs when the unassigned list changes (assigned flights leave)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    setSelectedIds((prev) => {
      const validIds = new Set(unassignedFlights.map((f) => f._id));
      const next = new Set<string>();
      for (const id of prev) {
        if (validIds.has(id)) next.add(id);
      }
      return next.size !== prev.size ? next : prev;
    });
  }, [unassignedFlights]);

  // ---------------------------------------------------------------------------
  // Selection helpers
  // ---------------------------------------------------------------------------

  // Flights that are currently selected (in flat-list order, for drag payload)
  const selectedFlights: IFlight[] = useMemo(
    () => unassignedFlights.filter((f) => selectedIds.has(f._id)),
    [unassignedFlights, selectedIds]
  );

  // ---------------------------------------------------------------------------
  // Group flights by aircraft registration (consistent with desk card layout)
  // ---------------------------------------------------------------------------

  const flightGroups = useMemo(() => {
    const groups = new Map<string, IFlight[]>();
    for (const flight of unassignedFlights) {
      const reg = flight.aircraft?.registration ?? "Unassigned";
      if (!groups.has(reg)) groups.set(reg, []);
      groups.get(reg).push(flight);
    }
    // Sort alphabetically, "Unassigned" last
    return Array.from(groups.entries())
      .sort(([a], [b]) => {
        if (a === "Unassigned") return 1;
        if (b === "Unassigned") return -1;
        return a.localeCompare(b);
      })
      .map(([registration, flights]) => ({ registration, flights }));
  }, [unassignedFlights]);

  /** Map registration → ordered flight IDs within that group (for shift-click range). */
  const groupFlightIdsMap: Map<string, string[]> = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const group of flightGroups) {
      map.set(
        group.registration,
        group.flights.map((f) => f._id)
      );
    }
    return map;
  }, [flightGroups]);

  const handleToggle = useCallback(
    (flightId: string, registration: string, groupIndex: number, shiftKey: boolean) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (shiftKey && lastClickedRef.current != null && lastClickedRef.current.registration === registration) {
          const groupIds = groupFlightIdsMap.get(registration);
          if (groupIds) {
            const start = Math.min(lastClickedRef.current.groupIndex, groupIndex);
            const end = Math.max(lastClickedRef.current.groupIndex, groupIndex);
            for (let i = start; i <= end; i++) {
              next.add(groupIds[i]);
            }
          }
        } else {
          if (next.has(flightId)) {
            next.delete(flightId);
          } else {
            next.add(flightId);
          }
        }
        lastClickedRef.current = { registration, groupIndex };
        return next;
      });
    },
    [groupFlightIdsMap]
  );

  const handleSelectAll = useCallback(() => {
    setSelectedIds(new Set(unassignedFlights.map((f) => f._id)));
  }, [unassignedFlights]);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
    lastClickedRef.current = null;
  }, []);

  // ---------------------------------------------------------------------------
  // Tooltip builder (mirrors desk card style)
  // ---------------------------------------------------------------------------

  const buildFlightTooltip = useCallback((flight: IFlight): React.ReactNode => {
    const fmt = "DD-MMM HH:mm [Z]";
    const off = toEpoch(flight.actualOff);
    const on = toEpoch(flight.actualOn);
    const lines: string[] = [];
    lines.push(`${flight.origin} → ${flight.destination}`);
    if (flight.aircraft?.registration) lines.push(`Aircraft: ${flight.aircraft.registration}`);
    if (off) lines.push(`ATD: ${moment.utc(off).format(fmt)}`);
    if (on) lines.push(`ATA: ${moment.utc(on).format(fmt)}`);
    return <Box sx={{ whiteSpace: "pre-line", fontSize: 12, lineHeight: 1.5 }}>{lines.join("\n")}</Box>;
  }, []);

  // ---------------------------------------------------------------------------
  // Map IFlight → IFlightChip
  // ---------------------------------------------------------------------------

  const toChip = useCallback((flight: IFlight): IFlightChip => {
    return {
      _id: flight._id,
      flightNumber: flight.flightNumber,
      origin: flight.origin,
      destination: flight.destination,
      flightType: flight.flightType,
      aircraft: flight.aircraft as any,
      removalType: flight.removalType,
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const toggle = (): void => setExpanded((prev) => !prev);
  const hasFlights = unassignedFlights.length > 0;
  const allSelected = hasFlights && selectedIds.size === unassignedFlights.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  return (
    <PanelRoot>
      {/* Thin clickable strip (always visible) */}
      <ToggleStrip onClick={toggle}>
        <Tooltip title={expanded ? "Collapse unassigned flights" : "Expand unassigned flights"} placement="left">
          <IconButton size="small" sx={{ p: 0.25 }}>
            {expanded ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        {!expanded && (
          <Box
            sx={{
              writingMode: "vertical-rl",
              textOrientation: "mixed",
              mt: 1,
              fontSize: "0.7rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: hasFlights ? "warning.main" : "text.secondary",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {hasFlights && <WarningAmberIcon sx={{ fontSize: 14 }} />}
            Unassigned ({unassignedFlights.length})
          </Box>
        )}
      </ToggleStrip>

      {/* Collapsible content */}
      <Collapse in={expanded} orientation="horizontal" unmountOnExit>
        <PanelContent>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              color={hasFlights ? "warning.main" : "text.primary"}
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              {hasFlights && <WarningAmberIcon sx={{ fontSize: 16 }} />}
              Unassigned Flights
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {unassignedFlights.length} flight{unassignedFlights.length !== 1 ? "s" : ""}
            </Typography>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Flights departing &amp; arriving within the next 24h that are not assigned to any desk. Check flights and
            drag the selection onto a desk, or drag individual flights. Shift-click to select a range.
          </Typography>

          <Divider sx={{ mb: 1 }} />

          {/* Select-all / clear toggle */}
          {hasFlights && (
            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
              <Tooltip title={allSelected ? "Deselect all" : someSelected ? "Select all" : "Select all"}>
                <IconButton
                  size="small"
                  onClick={allSelected ? handleClearSelection : handleSelectAll}
                  sx={{ p: 0.25, mr: 0.5 }}
                >
                  {allSelected ? (
                    <CheckBoxIcon fontSize="small" color="primary" />
                  ) : someSelected ? (
                    <IndeterminateCheckBoxIcon fontSize="small" color="primary" />
                  ) : (
                    <CheckBoxOutlineBlankIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              <Typography variant="caption" color="text.secondary">
                {selectedIds.size > 0 ? `${selectedIds.size} of ${unassignedFlights.length} selected` : "Select all"}
              </Typography>
            </Box>
          )}

          {/* Drag bar for selected flights */}
          {selectedFlights.length > 0 && (
            <SelectionBar selectedFlights={selectedFlights} onClear={handleClearSelection} />
          )}

          {unassignedFlights.length === 0 ? (
            <Box
              sx={{
                py: 4,
                textAlign: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                All upcoming flights are assigned to a desk.
              </Typography>
            </Box>
          ) : (
            flightGroups.map((group) => (
              <GroupBox key={group.registration}>
                <SectionLabel>{group.registration}</SectionLabel>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  {group.flights.map((flight, idx) => (
                    <DraggableFlightItem
                      key={flight._id}
                      flight={flight}
                      groupIndex={idx}
                      registration={group.registration}
                      isSelected={selectedIds.has(flight._id)}
                      selectedFlights={selectedFlights}
                      onToggle={handleToggle}
                      tooltipContent={buildFlightTooltip(flight)}
                      chipData={toChip(flight)}
                    />
                  ))}
                </Box>
              </GroupBox>
            ))
          )}
        </PanelContent>
      </Collapse>
    </PanelRoot>
  );
}
